import React, { useEffect } from 'react';
import { graphql, useFragment } from 'react-relay';
import Button from '@atlaskit/button';
import AddIcon from '@atlaskit/icon/glyph/add';
import { SkeletonItem } from '@atlaskit/menu';
import { useIntl } from '@atlassian/jira-intl';
import Placeholder from '@atlassian/jira-placeholder';
import type { async_softwareReleasesApprovers_Approvers$key } from '@atlassian/jira-relay/src/__generated__/async_softwareReleasesApprovers_Approvers.graphql';
import { lazyAfterPaint, useLazyPhase } from '@atlassian/react-loosely-lazy';
import { APPROVERS_SHOW_MORE_THRESHOLD } from './constants';
import messages from './messages';
import { ApproversCardWrapper, ApproversTitle, Header } from './index';

// eslint-disable-next-line jira/deprecations/no-rll-client-async-experiences
const LazyApprovers = lazyAfterPaint(
	() =>
		import(/* webpackChunkName: "async-releases-approvers" */ './index').then(
			(module) => module.Approvers,
		),
	{ ssr: false },
);

type Props = {
	jiraVersion: async_softwareReleasesApprovers_Approvers$key;
};

export const Approvers = ({ jiraVersion }: Props) => {
	const { startNextPhase } = useLazyPhase();
	useEffect(() => {
		startNextPhase();
	}, [startNextPhase]);

	const version = useFragment(
		graphql`
			fragment async_softwareReleasesApprovers_Approvers on JiraVersion {
				...ui_softwareReleasesApprovers_Approvers
			}
		`,
		jiraVersion,
	);

	const { formatMessage } = useIntl();

	const numberOfApproversToShow = APPROVERS_SHOW_MORE_THRESHOLD;

	return (
		<Placeholder
			name="releases-approvers"
			fallback={
				<ApproversCardWrapper>
					<Header>
						<ApproversTitle>{formatMessage(messages.approversTitle)}</ApproversTitle>
						<Button iconBefore={<AddIcon size="small" label="" />} appearance="subtle" />
					</Header>
					{[...Array(numberOfApproversToShow).keys()].map((i) => (
						<SkeletonItem key={i} isShimmering hasAvatar />
					))}
				</ApproversCardWrapper>
			}
		>
			<LazyApprovers jiraVersion={version} />
		</Placeholder>
	);
};
